import { makePrefsStore, channelAllowsInApp } from "./notificationPrefsCore";

// Shared constants for the DME order notification system (spec 2026-07-30).
// CATEGORY_BY_EVENT mirrors backend/lib/dmeNotifications.js EVENTS.outcomes.
// The channel matrix below is ORG-level: the email recipient is the practice's
// office inbox and that settings screen is supervisor-gated. The assigned-only
// helpers at the bottom are the exception — a PER-USER pref stored on
// user.custom and surfaced on a doctor-gated screen.

export const PREFS_UPDATED_EVENT = "dme-notif-prefs-updated";
export const PREFS_CACHE_KEY = "dmeNotifPrefs";

// Single source of truth for the category set: what the settings screen renders
// AND what it saves both come from here. Two parallel lists (one for render,
// one for save) previously meant a new category could render but never persist.
// `label`/`desc` are the i18n fallbacks; `emailLocked` marks the duty-critical
// category whose email cannot be switched off (backend enforces it too, in
// lib/dmeNotifications.js emailAllowed/coerceChannelForCategory).
export const DME_CATEGORY_DEFS = [
  {
    key: "orderProgress",
    label: "Order progress",
    desc: "IVR cleared and 1500 form cleared",
  },
  {
    key: "actionNeeded",
    label: "Action needed",
    desc: "IVR denied and 1500 form returned for corrections",
    emailLocked: true,
  },
  {
    key: "eobReceived",
    label: "EOB received",
    desc: "An Explanation of Benefits is attached to an order",
  },
];

export const DME_CATEGORIES = DME_CATEGORY_DEFS.map((c) => c.key);

export const CATEGORY_BY_EVENT = {
  ivr_reviewed: { completed: "orderProgress", denied: "actionNeeded" },
  form1500_reviewed: { cleared: "orderProgress", incomplete: "actionNeeded" },
  eob_received: { received: "eobReceived" },
};

export function categoryForEvent(eventType, outcome) {
  const byOutcome = CATEGORY_BY_EVENT[eventType];
  if (!byOutcome) return null;
  return byOutcome[outcome] || null;
}

const store = makePrefsStore({ cacheKey: PREFS_CACHE_KEY, updatedEvent: PREFS_UPDATED_EVENT });

export { channelAllowsInApp };
export const readCachedPrefs = store.readCachedPrefs;
export const writeCachedPrefs = store.writeCachedPrefs;
export const notifyPrefsUpdated = store.notifyUpdated;

export function assignedOnlyPrefEnabled(custom) {
  return custom?.dmeNotifAssignedOnly !== false;
}

// Assignable providers are the org users carrying the "doctor" group — the
// same population api.listDoctors() offers for order assignment. Deliberately
// NOT useAuth's hasGroup: that returns true for any administrator (and for a
// user with no groups array at all), while the assigned-only filter and its
// settings gate must track real assignability — everywhere, identically.
export function isAssignableProvider(user) {
  return !!user?.groups?.includes?.("doctor");
}

// Fails open: a missing assignee or viewer id never suppresses a toast.
export function passesAssignedOnlyFilter({ enabled, isDoctor, currentUserId, assignedToKey }) {
  if (!enabled || !isDoctor) return true;
  if (assignedToKey == null || String(assignedToKey) === "") return true;
  if (currentUserId == null) return true;
  return String(assignedToKey) === String(currentUserId);
}
